/**
 * Shared types + helpers for {@link WorkshopCalendarPicker}. The picker is
 * generic over a small, presentational {@link CalendarEvent} shape so any
 * call-site (the BON-202 cancel/rebook flow first; browse-all-workshops later)
 * can map its own domain rows onto the calendar without coupling the component
 * to a specific API type.
 */

/** Full detail bundle shown in the {@link CalendarEventPill}'s hover/focus
 *  tooltip (BON-142) — the day cell itself only has room for truncated text,
 *  so the tooltip surfaces the rest: workshop/tree name, venue, date, local
 *  time, tree species, seats-left, and price. OPTIONAL on {@link CalendarEvent}
 *  — callers that don't populate it (e.g. the BON-202 rebook flow) simply get
 *  no tooltip; the pill renders exactly as before. */
export interface CalendarEventTooltip {
  /** Workshop/tree type display name, e.g. "Dwarf Jade". */
  title: string;
  venueName: string;
  /** e.g. "Wed May 27". */
  dateLabel: string;
  /** e.g. "6:30p". */
  timeLabel: string;
  treeSpeciesName: string | null;
  /** e.g. "8 left" or "Sold out". */
  seatsLabel: string;
  /** e.g. "$65.00/seat". */
  priceLabel: string;
}

/** A pin-pointed, selectable workshop on the calendar. Purely presentational —
 *  callers map their domain objects (e.g. a RebookTarget) onto this. */
export interface CalendarEvent {
  id: string;
  /** UTC ISO instant — used only to bucket the event into a day cell + sort. */
  startsAt: string;
  /** Venue-local wall-clock WITH offset (e.g. `2026-05-10T17:00:00-04:00`).
   *  Rendered as a time label without timezone re-anchoring. */
  localStartsAt: string;
  /** Primary line — e.g. the workshop type ("Dwarf Jade"). Also drives the dot color. */
  title: string;
  /** Secondary line — e.g. venue + time ("Optimism Brewing · 6:30p"). */
  subtitle: string;
  /** Right-aligned seats hint, e.g. "10 left". Omitted when null. */
  seatsLabel: string | null;
  /** True → the event is shown but not selectable. */
  disabled: boolean;
  /** Why the event is disabled (shown as the event's title tooltip). */
  disabledReason?: string;
  /** Short standing label rendered as a chip ON the event, e.g. "Current" for
   *  the workshop a booking is being moved OFF (BON-146). Distinct from
   *  `disabledReason`, which explains a refusal: a badge is an identity, so it
   *  keeps its own chip instead of being swallowed by the seats line. Only the
   *  DENSE renderings honour it — the default pill has no room, and no
   *  non-dense call-site sets it. */
  badge?: string;
  /** Optional hover/focus tooltip detail bundle (BON-142). */
  tooltip?: CalendarEventTooltip;
}

/** Split a day's (already sorted) events into what fits the cell's fixed
 *  height and the rest, for the "+N more" affordance (BON-142) — pure so it's
 *  unit-testable without mounting the calendar. */
export function splitDayEvents(
  events: CalendarEvent[],
  maxVisible: number,
): { visible: CalendarEvent[]; hidden: CalendarEvent[] } {
  return { visible: events.slice(0, maxVisible), hidden: events.slice(maxVisible) };
}

/** A filter option for the optional filter bar (city / tree-type chips). Only
 *  rendered when the picker's `enableFilters` prop is on. */
export interface CalendarFilterOption {
  id: string;
  label: string;
}

/** A small, fixed palette for tree-type dots. Keyed by a stable hash of the
 *  event title so the same type always gets the same color. Mirrors the mock's
 *  muted, distinct-per-type swatches. */
const DOT_PALETTE = [
  '#6b8e5a', // moss
  '#b06a3b', // clay
  '#4a7c9b', // water
  '#8a6cab', // lavender
  '#c0392b', // danger-ish red
  '#3f7d5e', // pine
  '#b58a2e', // amber
  '#7d8a8f', // slate
] as const;

/** Deterministic color for a tree-type/title label. */
export function dotColor(title: string): string {
  let hash = 0;
  for (let i = 0; i < title.length; i += 1) {
    hash = (hash * 31 + title.charCodeAt(i)) | 0;
  }
  return DOT_PALETTE[Math.abs(hash) % DOT_PALETTE.length];
}

/** `#rrggbb` → `rgba(r, g, b, alpha)` — used to derive the dense-mode compact
 *  pill/card tint backgrounds (BON-142) from the SAME deterministic
 *  {@link dotColor} the dot itself uses, so a tree type's tint always matches
 *  its dot (in both the calendar cell and the day drawer). Returns the input
 *  unchanged if it isn't a 6-digit hex color. */
export function hexToRgba(hex: string, alpha: number): string {
  const m = /^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(hex);
  if (!m) return hex;
  const [r, g, b] = m.slice(1).map((h) => parseInt(h, 16));
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/** Time + venue for the dense-mode compact pill (BON-142). Prefers the
 *  event's `tooltip` detail (populated by the booking picker); falls back to
 *  splitting `subtitle` ("Venue · 6:30p") for callers that don't set one, so
 *  `dense` stays usable even without the optional tooltip bundle. */
export function venueAndTimeFromEvent(event: CalendarEvent): { time: string; venue: string } {
  if (event.tooltip) {
    return { time: event.tooltip.timeLabel, venue: event.tooltip.venueName };
  }
  const idx = event.subtitle.lastIndexOf(' · ');
  if (idx === -1) return { time: '', venue: event.subtitle };
  return { time: event.subtitle.slice(idx + 3), venue: event.subtitle.slice(0, idx) };
}

/**
 * Format a venue-local wall-clock string to a short time label WITHOUT timezone
 * conversion (the offset must NOT be re-anchored to the viewer's zone). Reads
 * the wall-clock hour/minute straight off the leading `…THH:MM` portion.
 * Returns e.g. "6:30p" / "9a"; falls back to "" on an unexpected shape.
 */
export function formatLocalTime(localStartsAt: string): string {
  const m = /T(\d{2}):(\d{2})/.exec(localStartsAt);
  if (!m) return '';
  const hours = Number(m[1]);
  const minutes = Number(m[2]);
  const ampm = hours >= 12 ? 'p' : 'a';
  const h12 = hours % 12 === 0 ? 12 : hours % 12;
  return minutes === 0 ? `${h12}${ampm}` : `${h12}:${String(minutes).padStart(2, '0')}${ampm}`;
}
